import { getAPICallFeature } from '@micro-frontend-react/employee-experience/lib/UsageTelemetryHelper';
import { CustomProperties, IHttpClient } from '@employee-experience/common/lib/Models';
import { SagaIterator } from 'redux-saga';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import { IUserProfile } from '../../models/IUserProfile';
import {
  IRequestLetterGenerationAction,
  IRequestLetterSubmissionAction,
  IRequestMetadataAction,
  MetadataActionTypes,
} from '../action-types/metadataActionTypes';
import {
  receiveLetterGeneration,
  receiveLetterGenerationFailed,
  receiveLetterSubmission,
  receiveLetterSubmissionFailed,
  receiveMetadata,
  receiveMetadataFailed,
} from '../actions/metadataActions';

export function getSharedHttpConfig(url: string): CustomProperties {
  const resource = __EVL_API_RESOURCE_ID__;
  const subscriptionKey = '';

  const httpConfig: CustomProperties = {
    url,
    resource,
  };

  if (subscriptionKey) {
    httpConfig.headers = {
      'Dwp-Apim-Subscription-Key': subscriptionKey,
    };
  }

  return httpConfig;
}

export const getProject = (state: any): IUserProfile => {
  return {
    alias: state?.userImpersonation.alias,
    allowedAva: state?.userImpersonation.allowedAva,
    allowedImpersonation: state?.userImpersonation.allowedImpersonation,
    companyCode: state?.userImpersonation.companyCode,
    costCenter: state?.userImpersonation.costCenter,
  };
};

export function* fetchLetterMetadata(action: IRequestMetadataAction): SagaIterator {
  const { feature, selectedTemplate } = action;
  const httpClient: IHttpClient = yield getContext('httpClient');

  try {
    const currentImpersonation = yield select(getProject);

    const response = yield call(
      [httpClient, httpClient.request],
      {
        ...getSharedHttpConfig(
          `${__EVL_API_BASE_URL__}api/v1.0/lettermetadata?templateID=${selectedTemplate.id}`
        ),
        headers: {
          ImpersonatedCompanyCode:
            currentImpersonation?.companyCode == null ||
            currentImpersonation?.companyCode == undefined
              ? ''
              : currentImpersonation?.companyCode,
          ImpersonatedCostCenter:
            currentImpersonation?.costCenter == null ||
            currentImpersonation?.costCenter == undefined
              ? ''
              : currentImpersonation?.costCenter,
        },
      },
      {},
      getAPICallFeature({ feature: 'EVL', subFeature: 'MetadataPanel' }, 'fetchLetterMetadata')
    );

    yield put(receiveMetadata(response.data));
  } catch (exa) {
    yield put(receiveMetadataFailed('something failed'));
  }
}

export function* fetchLetterPdf(action: IRequestLetterGenerationAction): SagaIterator {
  const { feature, letterGenerationRequest } = action;
  const httpClient: IHttpClient = yield getContext('httpClient');

  try {
    const currentImpersonation = yield select(getProject);

    const response = yield call(
      [httpClient, httpClient.request],
      {
        ...getSharedHttpConfig(`${__EVL_API_BASE_URL__}api/v1.0/generateLetter`),
        method: 'post',
        data: letterGenerationRequest,
        headers: {
          ImpersonatedCompanyCode:
            currentImpersonation?.companyCode == null ||
            currentImpersonation?.companyCode == undefined
              ? ''
              : currentImpersonation?.companyCode,
          ImpersonatedCostCenter:
            currentImpersonation?.costCenter == null ||
            currentImpersonation?.costCenter == undefined
              ? ''
              : currentImpersonation?.costCenter,
        },
      },
      {},
      getAPICallFeature({ feature: 'EVL', subFeature: 'MetadataPanel' }, 'fetchLetterPdf')
    );

    yield put(receiveLetterGeneration(response.data));
  } catch (exa) {
    yield put(receiveLetterGenerationFailed('something failed'));
  }
}

export function* requestLetterSubmission(action: IRequestLetterSubmissionAction): SagaIterator {
  const { feature, letterSubmissionRequest } = action;
  const httpClient: IHttpClient = yield getContext('httpClient');

  try {
    const currentImpersonation = yield select(getProject);

    const response = yield call(
      [httpClient, httpClient.request],
      {
        ...getSharedHttpConfig(`${__EVL_API_BASE_URL__}api/v1.0/submitLetter`),
        method: 'post',
        data: letterSubmissionRequest,
        headers: {
          ImpersonatedCompanyCode:
            currentImpersonation?.companyCode == null ||
            currentImpersonation?.companyCode == undefined
              ? ''
              : currentImpersonation?.companyCode,
          ImpersonatedCostCenter:
            currentImpersonation?.costCenter == null ||
            currentImpersonation?.costCenter == undefined
              ? ''
              : currentImpersonation?.costCenter,
        },
      },
      {},
      getAPICallFeature({ feature: 'EVL', subFeature: 'MetadataPanel' }, 'requestLetterSubmission')
    );

    yield put(receiveLetterSubmission(response.data));
  } catch (exa) {
    yield put(receiveLetterSubmissionFailed('something failed'));
  }
}

export function* letterMetadataSagas(): SagaIterator {
  yield all([
    takeLatest(MetadataActionTypes.REQUEST_METADATA, fetchLetterMetadata),
    takeLatest(MetadataActionTypes.REQUEST_LETTER_GENERATION, fetchLetterPdf),
    takeLatest(MetadataActionTypes.REQUEST_LETTER_SUBMISSION, requestLetterSubmission),
  ]);
}
